import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Crown } from 'lucide-react';
import { Button } from './Button';
interface LimitReachedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  limitType: 'quests' | 'collaborative' | 'categories' | 'focus' | 'export';
}
const limitMessages = {
  quests: {
    title: 'Quest Limit Reached',
    message: "You've reached the free tier limit of 5 active quests. Upgrade to Premium for unlimited quests!",
    icon: '🎯'
  },
  collaborative: {
    title: 'Collaboration Limit Reached',
    message: 'Free users can have 1 collaborative quest. Upgrade to Premium for unlimited collaboration!',
    icon: '👥'
  },
  categories: {
    title: 'Custom Categories Locked',
    message: 'Custom categories are a Premium feature. Upgrade to create your own categories with custom icons!',
    icon: '🎨'
  },
  focus: {
    title: 'Advanced Focus Locked',
    message: 'Advanced focus modes (Pomodoro, custom durations) are Premium features. Upgrade to unlock!',
    icon: '🧘'
  },
  export: {
    title: 'Export Feature Locked',
    message: 'Data export is a Premium feature. Upgrade to export your quests and progress!',
    icon: '📊'
  }
};
export function LimitReachedModal({
  isOpen,
  onClose,
  onUpgrade,
  limitType
}: LimitReachedModalProps) {
  const {
    title,
    message,
    icon
  } = limitMessages[limitType];
  return <AnimatePresence data-id="element-344">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6" onClick={onClose} data-id="element-345">
          <motion.div initial={{
        scale: 0.95,
        y: 20
      }} animate={{
        scale: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        y: 20
      }} className="bg-white dark:bg-[#252936] rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-346">
            {/* Icon */}
            <div className="flex justify-center mb-6" data-id="element-347">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-full flex items-center justify-center relative" data-id="element-348">
                <span className="text-4xl" data-id="element-349">{icon}</span>
                <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-gradient-to-br from-game-yellow to-game-coral rounded-full flex items-center justify-center border-4 border-white dark:border-[#252936]" data-id="element-350">
                  <Lock className="w-5 h-5 text-white" data-id="element-351" />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="text-center mb-6" data-id="element-352">
              <h2 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white mb-3" data-id="element-353">
                {title}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 font-quicksand leading-relaxed" data-id="element-354">
                {message}
              </p>
            </div>

            {/* Premium Features Preview */}
            <div className="bg-gradient-to-br from-game-bg-purple to-game-bg-yellow dark:from-purple-900/20 dark:to-yellow-900/20 rounded-2xl p-4 mb-6" data-id="element-355">
              <div className="flex items-center gap-2 mb-3" data-id="element-356">
                <Crown className="w-5 h-5 text-game-purple" data-id="element-357" />
                <span className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-358">
                  Premium Benefits:
                </span>
              </div>
              <ul className="space-y-2 text-sm font-quicksand text-gray-700 dark:text-gray-300" data-id="element-359">
                <li className="flex items-center gap-2" data-id="element-360">
                  <div className="w-1.5 h-1.5 bg-game-purple rounded-full" data-id="element-361"></div>
                  Unlimited quests & collaborations
                </li>
                <li className="flex items-center gap-2" data-id="element-362">
                  <div className="w-1.5 h-1.5 bg-game-teal rounded-full" data-id="element-363"></div>
                  Advanced focus modes & AI features
                </li>
                <li className="flex items-center gap-2" data-id="element-364">
                  <div className="w-1.5 h-1.5 bg-game-coral rounded-full" data-id="element-365"></div>
                  Custom themes & data export
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex gap-3" data-id="element-366">
              <Button variant="secondary" onClick={onClose} className="flex-1" data-id="element-367">
                Maybe Later
              </Button>
              <Button onClick={onUpgrade} className="flex-1 bg-gradient-to-r from-game-purple to-game-teal" icon={<Crown className="w-5 h-5" data-id="element-369" />} data-id="element-368">
                Upgrade Now
              </Button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}